import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useVoicePlayback } from '@/model/contexts/VoicePlaybackContext'

export const VOICE_CLIP_MAX_DURATION_MS = 60_000
const VOICE_CLIP_MIN_DURATION_MS = 1000

// AAC-LC: the codec every iPhone and Android player reads. A bare `audio/mp4` lets Chrome pick Opus.
const VOICE_CLIP_MIME_TYPE = 'audio/mp4;codecs=mp4a.40.2'

export const isVoiceRecordingSupported = () =>
  typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(VOICE_CLIP_MIME_TYPE)

export type VoiceRecorderError = 'denied' | 'notFound' | 'busy'

export interface VoiceRecording {
  blob: Blob
  url: string
  durationMs: number
}

export type VoiceRecorderState =
  | { status: 'idle'; error?: VoiceRecorderError }
  | { status: 'starting' }
  | { status: 'recording'; elapsedMs: number }
  | { status: 'preview'; recording: VoiceRecording }

const recorderErrorOf = (error: unknown): VoiceRecorderError => {
  const name = error instanceof DOMException ? error.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'notFound'
  return 'busy'
}

export const useVoiceRecorder = () => {
  const id = useId()
  const playback = useVoicePlayback()
  const [state, setState] = useState<VoiceRecorderState>({ status: 'idle' })
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const discardRef = useRef(false)
  const startAttemptRef = useRef(0)
  const previewUrlRef = useRef<string | null>(null)
  const [delivery, setDelivery] = useState<{ url: string; failed: boolean } | null>(null)

  const releaseStream = useCallback(() => {
    if (tickerRef.current !== null) {
      globalThis.clearInterval(tickerRef.current)
      tickerRef.current = null
    }
    streamRef.current?.getTracks().forEach((track) => {
      track.stop()
    })
    streamRef.current = null
  }, [])

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }, [])

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder?.state === 'recording') {
      recorder.stop()
    }
  }, [])

  const discard = useCallback(() => {
    startAttemptRef.current++
    playback.releaseRecorder(id)
    if (recorderRef.current) {
      discardRef.current = true
      stop()
      return
    }
    revokePreview()
    setState({ status: 'idle' })
  }, [id, playback, revokePreview, stop])

  const stopIntoPreview = useCallback(() => {
    if (recorderRef.current) {
      stop()
      return
    }
    discard()
  }, [discard, stop])

  const send = useCallback(
    async (recording: VoiceRecording, deliver: (recording: VoiceRecording) => Promise<void>) => {
      setDelivery({ url: recording.url, failed: false })
      try {
        await deliver(recording)
      } catch {
        setDelivery((current) => (current?.url === recording.url ? { url: recording.url, failed: true } : current))
        return
      }
      // The upload can outlive its clip: deleted meanwhile, then replaced by a new recording that must survive.
      if (previewUrlRef.current === recording.url) discard()
    },
    [discard]
  )

  const start = useCallback(async () => {
    if (recorderRef.current || !isVoiceRecordingSupported()) return
    const attempt = ++startAttemptRef.current
    playback.stopAll()
    // Claimed before the microphone answers, so the last recording started wins even while an earlier one waits.
    playback.claimRecorder(id, { discard, stopIntoPreview })
    setState({ status: 'starting' })
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      if (attempt === startAttemptRef.current) {
        playback.releaseRecorder(id)
        setState({ status: 'idle', error: recorderErrorOf(error) })
      }
      return
    }
    if (attempt !== startAttemptRef.current) {
      stream.getTracks().forEach((track) => {
        track.stop()
      })
      return
    }
    streamRef.current = stream
    const recorder = new MediaRecorder(stream, { mimeType: VOICE_CLIP_MIME_TYPE, audioBitsPerSecond: 64_000 })
    recorderRef.current = recorder
    discardRef.current = false
    const chunks: Blob[] = []
    const startedAt = performance.now()

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    })
    recorder.addEventListener('stop', () => {
      const durationMs = Math.min(performance.now() - startedAt, VOICE_CLIP_MAX_DURATION_MS)
      recorderRef.current = null
      playback.releaseRecorder(id)
      releaseStream()
      if (discardRef.current || durationMs < VOICE_CLIP_MIN_DURATION_MS || chunks.length === 0) {
        setState({ status: 'idle' })
        return
      }
      const blob = new Blob(chunks, { type: 'audio/mp4' })
      const url = URL.createObjectURL(blob)
      previewUrlRef.current = url
      setState({ status: 'preview', recording: { blob, url, durationMs: Math.round(durationMs) } })
    })

    recorder.start()
    setState({ status: 'recording', elapsedMs: 0 })
    tickerRef.current = globalThis.setInterval(() => {
      const elapsedMs = performance.now() - startedAt
      if (elapsedMs >= VOICE_CLIP_MAX_DURATION_MS) {
        stop()
        return
      }
      setState({ status: 'recording', elapsedMs })
    }, 100)
  }, [discard, id, playback, releaseStream, stop, stopIntoPreview])

  useEffect(
    () => () => {
      startAttemptRef.current++
      discardRef.current = true
      playback.releaseRecorder(id)
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
      releaseStream()
      revokePreview()
    },
    [id, playback, releaseStream, revokePreview]
  )

  const deliveryOfPreview = state.status === 'preview' && delivery?.url === state.recording.url ? delivery : null

  return {
    state,
    isSupported: isVoiceRecordingSupported(),
    sending: deliveryOfPreview?.failed === false,
    sendFailed: deliveryOfPreview?.failed === true,
    start,
    stop,
    discard,
    send,
  }
}

export type VoiceRecorder = ReturnType<typeof useVoiceRecorder>
